import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

type Props = {
  /** Arte em pé (celular). Se faltar, usa a do computador. */
  mobile?: string | null;
  /** Arte deitada (computador). Se faltar, usa a do celular. */
  desktop?: string | null;
  alt: string;
  /** Proporção reservada no celular antes da arte carregar. Padrão 4:5. */
  proporcao?: string;
  /** Proporção reservada no computador antes da arte carregar. Padrão 16:9. */
  proporcaoDesktop?: string;
  className?: string;
  /** Carrega sem esperar rolagem. */
  prioridade?: boolean;
  /** Preenche a altura do elemento pai (quadro sem proporção própria). */
  preencher?: boolean;
  /**
   * Caixa acompanha a proporção REAL da arte (img w-full h-auto), com teto de
   * 75vh. O fundo desfocado só entra quando a arte passa desse teto e é
   * reduzida. Enquanto carrega, reserva 4:5 com fundo cinza.
   */
  natural?: boolean;
  /** Quando existe, o quadro inteiro vira botão (abre em tela cheia). */
  onAbrir?: () => void;
  children?: ReactNode;
};

type QuadroProps = {
  src: string;
  alt: string;
  proporcao?: string;
  preencher: boolean;
  natural: boolean;
  prioridade: boolean;
  className: string;
  onAbrir?: () => void;
  children?: ReactNode;
};

const TETO_VH = 0.75;

/**
 * REGRA ÚNICA das artes de evento:
 * - A arte NUNCA é cortada.
 * - Com `natural`, a caixa cresce no formato real do cartaz (sem faixas
 *   vazias sobrando), limitada a 75vh. Só acima disso a arte é reduzida
 *   inteira (object-contain) com o fundo desfocado preenchendo as sobras.
 * - Antes de carregar: caixa 4:5 cinza (a tela não pula). Se der erro, o
 *   cinza permanece.
 * - No celular usa a arte em pé; no computador usa a arte deitada. Se uma
 *   faltar, a outra assume.
 */
function Quadro({
  src,
  alt,
  proporcao,
  preencher,
  natural,
  prioridade,
  className,
  onAbrir,
  children,
}: QuadroProps) {
  const usarNatural = natural && !preencher;
  const [carregou, setCarregou] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const [passouDoTeto, setPassouDoTeto] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const reservando = usarNatural && !carregou && !falhou;

  const medir = (el: HTMLImageElement) => {
    if (!el.naturalWidth) return;
    const alturaNatural = el.clientWidth * (el.naturalHeight / el.naturalWidth);
    setPassouDoTeto(alturaNatural > window.innerHeight * TETO_VH + 1);
  };

  const concluirCarregamento = (el: HTMLImageElement) => {
    setCarregou(true);
    medir(el);
  };

  const caixa = [
    'relative w-full overflow-hidden',
    usarNatural ? 'bg-background-200' : 'bg-foreground-950',
    usarNatural ? '' : 'rounded-3xl',
    preencher ? 'h-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Reserva do tamanho antes de a arte aparecer; some assim que ela carrega.
  const estilo = usarNatural
    ? reservando
      ? { aspectRatio: proporcao }
      : undefined
    : preencher
      ? undefined
      : { aspectRatio: proporcao };

  // Erro ao carregar: mantém o placeholder cinza do tamanho reservado.
  if (usarNatural && falhou) {
    return (
      <div className={caixa} style={{ aspectRatio: proporcao }} aria-hidden>
        <div className="absolute inset-0 flex items-center justify-center bg-background-200">
          <i className="ri-image-line text-[22px] text-foreground-400" aria-hidden />
        </div>
      </div>
    );
  }

  const fundo = usarNatural ? passouDoTeto && carregou : true;

  const conteudo = (
    <>
      {/* fundo: a própria arte, desfocada, cobrindo as sobras (só quando a
          arte foi reduzida para caber no teto de 75vh) */}
      {fundo && (
        <img
          src={src}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-60 blur-xl"
          loading={prioridade ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
      {/* frente: a arte inteira, centralizada, sem cortar nada */}
      <img
        ref={(el) => {
          imgRef.current = el;
          if (el && el.complete && el.naturalWidth > 0) concluirCarregamento(el);
        }}
        src={src}
        alt={alt}
        title={alt}
        onLoad={(event) => concluirCarregamento(event.currentTarget)}
        onError={() => setFalhou(true)}
        className={
          usarNatural
            ? 'relative block h-auto w-full max-h-[75vh] object-contain object-center'
            : 'relative h-full w-full object-contain object-center'
        }
        loading={prioridade ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={prioridade ? 'high' : undefined}
      />
      {children}
    </>
  );

  if (onAbrir) {
    return (
      <button
        type="button"
        onClick={onAbrir}
        aria-label={`Ver ${alt} em tela cheia`}
        className={`${caixa} block cursor-zoom-in`}
        style={estilo}
      >
        {conteudo}
      </button>
    );
  }

  return (
    <div className={caixa} style={estilo}>
      {conteudo}
    </div>
  );
}

export function CapaEvento({
  mobile,
  desktop,
  alt,
  proporcao = '4/5',
  proporcaoDesktop = '16/9',
  className = '',
  prioridade = false,
  preencher = false,
  natural = false,
  onAbrir,
  children,
}: Props) {
  const doCelular = mobile || desktop || null;
  const doComputador = desktop || mobile || null;
  const caixaNatural = natural && !preencher;

  if (!doCelular) {
    return (
      <div
        className={[
          'w-full rounded-3xl',
          caixaNatural ? 'bg-background-200' : 'bg-gradient-to-br from-primary-600 to-primary-400',
          preencher ? 'h-full' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={preencher ? undefined : { aspectRatio: proporcao }}
        aria-hidden
      />
    );
  }

  return (
    <div className={`w-full ${preencher ? 'h-full' : ''}`.trim()}>
      {/* celular: arte em pé */}
      <div className={preencher ? 'h-full md:hidden' : 'md:hidden'}>
        <Quadro
          src={doCelular}
          alt={alt}
          proporcao={proporcao}
          preencher={preencher}
          natural={natural}
          prioridade={prioridade}
          className={className}
          onAbrir={onAbrir}
        >
          {children}
        </Quadro>
      </div>
      {/* computador: arte deitada */}
      <div className={preencher ? 'hidden h-full md:block' : 'hidden md:block'}>
        <Quadro
          src={doComputador}
          alt={alt}
          proporcao={proporcaoDesktop}
          preencher={preencher}
          natural={natural}
          prioridade={prioridade}
          className={className}
          onAbrir={onAbrir}
        >
          {children}
        </Quadro>
      </div>
    </div>
  );
}

export default CapaEvento;